/**
 * LibraryRecommendationsBanner — v3.12.1
 *
 * Surfaces the top-N most relevant Past Performances and Winning Proposals
 * from the org's Client Library at the top of the pursuit's Manage
 * Documents page. Each row carries a relevance score, match-reason chips,
 * and a one-click Pull button that reuses the v3.12.0 pull-from-kb
 * endpoint (so the actual files come along, not just metadata).
 *
 * Feels like a "warm draft" prompt: writers see, on Day 1 of a pursuit,
 * "you have 3 reusable PPs and 2 reusable proposals that match this RFP."
 */
import { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Sparkles,
  Briefcase,
  Trophy,
  ArrowDownToLine,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (n) => {
  if (n == null || isNaN(Number(n))) return null;
  const v = Number(n);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${v}`;
};

// Color the score chip from cold (low) to hot (high) — quick visual signal.
const scoreTone = (score) => {
  if (score >= 60) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (score >= 35) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

export default function LibraryRecommendationsBanner({
  pursuitId,
  authToken,
  onPulled,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pullingId, setPullingId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!pursuitId || !authToken) {
      setLoading(false);
      return;
    }
    const fetchRecs = async () => {
      try {
        const res = await fetch(
          `${API}/api/pursuit-kb/${pursuitId}/library-recommendations?limit=3`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (res.ok) {
          setData(await res.json());
        }
      } finally {
        setLoading(false);
      }
    };
    fetchRecs();
  }, [pursuitId, authToken]);

  const handlePull = async (entityType, entityId, label) => {
    setPullingId(entityId);
    try {
      const res = await fetch(
        `${API}/api/pursuit-kb/${pursuitId}/documents/pull-from-kb`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entity_type: entityType,
            entity_id: entityId,
            include_attachments: true,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Pull failed');
      }
      const result = await res.json();
      const fileCount = (result.files_copied || []).length;
      toast.success(
        fileCount
          ? `Pulled "${label}" + ${fileCount} file${fileCount > 1 ? 's' : ''}`
          : `Pulled "${label}"`,
      );
      onPulled?.();
    } catch (err) {
      toast.error(err.message || 'Pull failed');
    } finally {
      setPullingId(null);
    }
  };

  // Hide the banner entirely while loading or when there are no matches.
  // The Manage Documents page is dense already — empty bands add noise.
  if (loading || !data) return null;
  const totalRecs =
    (data.past_performances?.length || 0) + (data.winning_proposals?.length || 0);
  if (totalRecs === 0) return null;

  return (
    <Card
      className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white shadow-sm"
      data-testid="library-recommendations-banner"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Sparkles className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3
                  className="text-sm font-semibold text-slate-900"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {totalRecs} reusable item{totalRecs > 1 ? 's' : ''} from your Client Library match this pursuit
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Scanned {data.total_pp_scanned} past performance
                  {data.total_pp_scanned === 1 ? '' : 's'} and{' '}
                  {data.total_wp_scanned} winning proposal
                  {data.total_wp_scanned === 1 ? '' : 's'}
                  {data.pursuit_signals?.naics_code && (
                    <> • NAICS <span className="font-mono">{data.pursuit_signals.naics_code}</span></>
                  )}
                  {data.pursuit_signals?.agency && (
                    <> • {data.pursuit_signals.agency}</>
                  )}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCollapsed(!collapsed)}
                className="text-slate-400 hover:text-slate-700 flex-shrink-0"
                data-testid="library-recs-toggle"
              >
                {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </Button>
            </div>

            {!collapsed && (
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                {/* Past Performances column */}
                {(data.past_performances || []).length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5 flex items-center gap-1">
                      <Briefcase className="w-3 h-3" />
                      Past Performance
                    </p>
                    <div className="space-y-1.5">
                      {data.past_performances.map((pp) => (
                        <RecommendationRow
                          key={pp.id}
                          item={pp}
                          entityType="past-performances"
                          subtitle={[
                            pp.agency,
                            formatCurrency(pp.contract_value),
                            pp.cpars_rating && pp.cpars_rating !== 'NOT_RATED' ? pp.cpars_rating.replace('_', ' ') : null,
                          ].filter(Boolean).join(' · ')}
                          pullingId={pullingId}
                          onPull={handlePull}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Winning Proposals column */}
                {(data.winning_proposals || []).length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5 flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      Winning Proposals
                    </p>
                    <div className="space-y-1.5">
                      {data.winning_proposals.map((wp) => (
                        <RecommendationRow
                          key={wp.id}
                          item={wp}
                          entityType="winning-proposals"
                          subtitle={[
                            wp.agency,
                            formatCurrency(wp.contract_value),
                            wp.naics_code,
                          ].filter(Boolean).join(' · ')}
                          pullingId={pullingId}
                          onPull={handlePull}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RecommendationRow({ item, entityType, subtitle, pullingId, onPull }) {
  return (
    <div
      className="flex items-start gap-2 p-2.5 bg-white border border-slate-200 rounded-md hover:border-amber-300 transition-colors"
      data-testid={`lib-rec-row-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <Badge
            variant="outline"
            className={`text-[10px] font-mono ${scoreTone(item.score)}`}
            title={`Relevance score: ${item.score}/100`}
          >
            {item.score}
          </Badge>
          <p className="text-sm font-medium text-slate-900 truncate flex-1">
            {item.title}
          </p>
        </div>
        {subtitle && (
          <p className="text-xs text-slate-500 truncate">{subtitle}</p>
        )}
        {(item.match_reasons || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {item.match_reasons.slice(0, 4).map((r, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-800 rounded border border-amber-100"
              >
                {r}
              </span>
            ))}
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="flex-shrink-0 h-8"
        disabled={pullingId === item.id}
        onClick={() => onPull(entityType, item.id, item.title)}
        data-testid={`lib-rec-pull-${item.id}`}
      >
        {pullingId === item.id ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <ArrowDownToLine className="w-3.5 h-3.5" />
        )}
      </Button>
    </div>
  );
}
